import {
  Box,
  Button,
  Center,
  Divider,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react'
import { PhotoVideo } from '../icons/icons'

import { Camera, CameraPermissionType, CameraResultType } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { Filesystem } from '@capacitor/filesystem'
import loadImage from 'blueimp-load-image'
import { useEffect, useRef, useState } from 'react'
import ImageCropModal from '../../components/modals/ImageCropModal' // Import the crop modal
import handleToast from '../toast/handleToast'
import MediaPermissionRequiredModal, {
  hasDeniedMediaPermissions,
  MediaPermissionDeniedError,
  requestFileSystemPermission,
} from './MediaPermissionRequiredModal'

function dataURLToFile(dataUrl: string, fileName: string) {
  try {
    const [meta, base64] = dataUrl.split(',')
    const mimeMatch = meta.match(/:(.*?);/)
    if (!mimeMatch) {
      throw new Error('Invalid data URL format')
    }

    const mime = mimeMatch[1]
    const bstr = atob(base64)
    let n = bstr.length
    const u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    return new File([u8arr], fileName, { type: mime })
  } catch (error) {
    console.error('Error converting data URL to file:', error)
    return null
  }
}

const verifyCameraPermissions = async () => {
  return !(await hasDeniedMediaPermissions({ fileSystem: true }))
}

const requestCameraPermissions = async ({ includeFileSystem = true }: { includeFileSystem?: boolean } = {}) => {
  if (Capacitor.isNativePlatform() === false) {
    return true
  }

  const res = await Camera.checkPermissions()

  const requestCamera = res.camera !== 'granted' && res.camera !== 'denied'
  // Don't request photos permission if we already have limited access
  const requestPhotos = res.photos !== 'granted' && res.photos !== 'limited' && res.photos !== 'denied'

  const requests: CameraPermissionType[] = []

  if (requestCamera) {
    requests.push('camera')
  }

  if (requestPhotos) {
    requests.push('photos')
  }

  let ask = null
  if (requests.length != 0) {
    ask = await Camera.requestPermissions({ permissions: requests })
  }

  const cameraPermission = res.camera === 'granted' || ask?.camera === 'granted'
  // Allow both "granted" and "limited" photo access - limited access is sufficient for our use case
  const photoLibraryPermission =
    res.photos === 'granted' || res.photos === 'limited' || ask?.photos === 'granted' || ask?.photos === 'limited'
  if (!cameraPermission || !photoLibraryPermission) {
    return false
  }

  const fileSystemPermission = includeFileSystem ? await requestFileSystemPermission() : true

  return fileSystemPermission
}

export const useCamera = async (): Promise<{ file: File; filePath?: string; webPath?: string }> => {
  if (Capacitor.isNativePlatform()) {
    const permissionsGranted = await requestCameraPermissions({ includeFileSystem: false })
    if (!permissionsGranted) {
      throw new MediaPermissionDeniedError()
    }
  }

  // Use Uri result type to get native file path WITHOUT loading into memory
  // On mobile, this avoids the bottleneck of loading the entire file
  const image = await Camera.getPhoto({
    quality: 100, // Full quality since we're not loading into memory
    allowEditing: false,
    resultType: Capacitor.isNativePlatform() ? CameraResultType.Uri : CameraResultType.DataUrl,
    // Don't specify width/height on mobile to avoid processing
    ...(Capacitor.isNativePlatform() ? {} : { width: 500, height: 500 }),
  })

  if (Capacitor.isNativePlatform() && image.path) {
    // Native platform: load image data so we have a real File for preview and FormData upload
    // (e.g. Become Host photo ID uses putHostPhoto with FormData; empty File would send nothing)
    const nativePath = image.path
    const fileName = nativePath.split('/').pop() || `image.${image.format || 'jpg'}`

    const extension = fileName.split('.').pop()?.toLowerCase() || image.format || 'jpg'
    const mimeType =
      extension === 'jpg' || extension === 'jpeg' ? 'image/jpeg' :
        extension === 'png' ? 'image/png' :
          extension === 'gif' ? 'image/gif' :
            extension === 'webp' ? 'image/webp' :
              extension === 'mp4' ? 'video/mp4' :
                extension === 'mov' ? 'video/quicktime' :
                  'image/jpeg'

    // Fetch the image via webPath so we get actual bytes for preview and upload
    const response = await fetch(image.webPath ?? '')
    if (!response.ok) {
      throw new Error('Failed to read captured image')
    }
    const blob = await response.blob()
    const file = new File([blob], fileName, { type: mimeType })

    return { file, filePath: nativePath, webPath: image.webPath }
  } else {
    // Web platform: Use DataUrl (no native path available)
    const createdFile = dataURLToFile(image.dataUrl ?? '', 'image.' + image.format)
    if (!createdFile) {
      throw new Error('Failed to create file from data URL')
    }
    return { file: createdFile }
  }
}
const correctImageOrientation = (file: File): Promise<File | null> => {
  return new Promise((resolve) => {
    try {
      loadImage(
        file,
        (canvas: any) => {
          if (canvas.type === 'error' || !canvas?.toDataURL) {
            resolve(file)
            return
          }
          try {
            const dataURL = canvas.toDataURL(file.type)
            const correctedFile = dataURLToFile(dataURL, `cor_${file.name}`)
            resolve(correctedFile)
          } catch {
            resolve(file)
          }
        },
        {
          canvas: true,
          orientation: true,
        }
      )
    } catch {
      resolve(file)
    }
  })
}

const ImageSelectModal = ({
  isOpen,
  onClose,
  onUpload,
}: {
  isOpen: boolean
  onClose: () => void
  onUpload: (file: File, filePath?: string, webPath?: string) => void
}) => {
  const toast = useToast()
  const [sharingAllowed, setSharingAllowed] = useState(true)
  const [image, setImage] = useState<File | null>(null) // Add avatar state
  const [imageWebPath, setImageWebPath] = useState<string | undefined>(undefined) // Track webPath for preview
  const [isCropModalOpen, setIsCropModalOpen] = useState(false)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)

  useEffect(() => {
    verifyCameraPermissions().then((res) => {
      setSharingAllowed(res)
    })
  }, [isOpen]) // Re-check when modal opens

  const buttonRef = useRef<HTMLInputElement | null>(null)

  const handleUploadClick = async () => {
    // If not native platform, just open file picker
    if (Capacitor.isNativePlatform() === false) {
      buttonRef.current?.click()
      return
    }

    // Always try to request permissions first (even if previously denied,
    // the system might allow re-requesting in some cases, or the user might have
    // reset permissions in settings)
    const permissionsGranted = await requestCameraPermissions()

    if (permissionsGranted) {
      // Permissions granted, proceed with file selection
      setSharingAllowed(true)
      buttonRef.current?.click()
      return
    }

    // If permissions weren't granted, check the current status
    const cameraPerms = await Camera.checkPermissions()
    const fileSystemPerms = await Filesystem.checkPermissions()

    const cameraDenied = cameraPerms.camera === 'denied'
    const photosDenied = cameraPerms.photos === 'denied'
    const fileSystemDenied = fileSystemPerms.publicStorage === 'denied'

    // If any permission is denied, we can't show the native popup again
    // The user must go to settings manually
    if (cameraDenied || photosDenied || fileSystemDenied) {
      setSharingAllowed(false)
      setIsPermissionModalOpen(true)
    } else {
      // Permissions might be in a requestable state, but request failed
      // This could happen if the user dismissed the popup
      handleToast(
        toast,
        'Please allow camera and gallery access when prompted, or enable permissions in your device settings.'
      )
      setSharingAllowed(false)
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // HTML file input doesn't provide native paths, so filePath is undefined
      // Check if the file is a video
      if (file.type.startsWith('video/')) {
        // No need to correct orientation for videos, just pass the file
        onUpload(file)
      } else if (file.type.startsWith('image/')) {
        // Show cropper immediately with preview (non-blocking)
        // This allows UI to appear instantly while orientation correction happens in background
        const previewUrl = URL.createObjectURL(file)
        setImageWebPath(previewUrl)
        setImage(file)
        setIsCropModalOpen(true)

        // Correct orientation in background (non-blocking for UI)
        correctImageOrientation(file)
          .then((correctedFile) => {
            if (correctedFile) {
              // Update with corrected file
              setImage(correctedFile)
              // Clean up old preview URL and create new one
              if (previewUrl) {
                URL.revokeObjectURL(previewUrl)
              }
              const newPreviewUrl = URL.createObjectURL(correctedFile)
              setImageWebPath(newPreviewUrl)
            }
          })
          .catch((error) => {
            console.error('Error correcting image orientation:', error)
            // Keep original file if correction fails
          })
      } else {
        console.error('Unsupported file type.')
      }
    }
  }

  return (
    <>
      <MediaPermissionRequiredModal
        isOpen={isPermissionModalOpen}
        onClose={() => setIsPermissionModalOpen(false)}
      />
      <ImageCropModal
        image={imageWebPath || (image ? URL.createObjectURL(image) : '')}
        isOpen={isCropModalOpen}
        isLoading={false}
        onClose={() => {
          setIsCropModalOpen(false)
          // Clean up preview URL when closing
          if (imageWebPath && imageWebPath.startsWith('blob:')) {
            URL.revokeObjectURL(imageWebPath)
          }
          setImageWebPath(undefined)
        }}
        onSave={(file: File | null) => {
          setIsCropModalOpen(false)
          // Clean up preview URL
          if (imageWebPath && imageWebPath.startsWith('blob:')) {
            URL.revokeObjectURL(imageWebPath)
          }
          setImageWebPath(undefined)
          if (file) {
            onUpload(file) // Upload the cropped image
          }
        }}
      />
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent mx="10px">
          <ModalBody p="0">
            <Center>
              <Text variant="title" fontSize="2xl" fontWeight="500" mb="20px">
                Create a new post
              </Text>
            </Center>
            <Divider />

            <Center
              h="360px"
              onDragOver={(e) => {
                e.preventDefault()
              }}
              onDrop={(e) => {
                e.preventDefault()
                const file = e.dataTransfer.files?.[0]
                if (file) {
                  onUpload(file) // Drag and drop doesn't provide native paths
                }
                // if (file && file.type.startsWith('image/')) {
                //     onUpload(file);
                // } else {
                //     handleToast(toast,'The file must be an image')
                // }
              }}
            >
              <Box>
                <Center>
                  <Icon viewBox="0 0 98 76" color="#292D32" w="98px" h="76px" fill="none">
                    <PhotoVideo />
                  </Icon>
                </Center>
                {sharingAllowed ? (
                  <Text variant="title" fontSize="md" fontWeight="500" mt="20px">
                    Drag photos and videos here
                  </Text>
                ) : (
                  <Text variant="title" fontSize="md" fontWeight="500" mt="20px">
                    Please enable your camera and gallery permissions
                  </Text>
                )}

                <Center mt="20px" pos="relative">
                  <Button onClick={handleUploadClick} w="120px" variant="heavy">
                    Upload
                  </Button>
                  <input
                    // style={{
                    //     position:'absolute',
                    //     width:'120px', height:'40px',
                    //     opacity:'0'
                    // }}
                    style={{ display: 'none' }}
                    ref={buttonRef}
                    id="file-upload"
                    type="file"
                    accept="image/jpeg, image/png, video/mp4, video/quicktime, video/x-msvideo, video/x-matroska"
                    onChange={(e) => {
                      handleFileChange(e)
                    }}
                  />
                </Center>
              </Box>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ImageSelectModal
